<template>
  <div class="home">

    <!-- ─── 01 Hero：先看到東西，再解釋概念 ─── -->
    <section class="home-hero">
      <div class="home-container home-hero-grid">
        <div class="home-hero-text">
          <!-- 主標在逗號處硬斷行，避免自然換行把「讓 AI 進入你的流程」從中間切開 -->
          <h1 class="home-hero-title" v-html="t('pages.home.hero.title')"></h1>
          <p class="home-hero-sub" v-html="t('pages.home.hero.subtitle')"></p>
          <p class="home-hero-audience">{{ t('pages.home.hero.audience') }}</p>
          <div class="home-hero-actions">
            <!-- 未登入給註冊 CTA，已登入直接進後台 -->
            <a v-if="!authStore.isLoggedIn" :href="localePath('/signup')" class="landing-btn">
              {{ t('pages.home.hero.cta-primary') }}
            </a>
            <a v-else href="/app" class="landing-btn">
              {{ t('common.enter-dashboard') }}
            </a>
            <!-- 門檻較低的第二條路：還沒準備好註冊的人跳到影片 -->
            <a href="#demo" class="landing-btn landing-btn-outline">
              {{ t('pages.home.hero.cta-secondary') }}
            </a>
          </div>
        </div>
        <div class="home-hero-shot">
          <!-- 真實產品介面，不是插圖：老闆看不到東西就當作還沒做出來 -->
          <img :src="heroShot" :alt="t('pages.home.hero.screenshot-alt')" class="home-hero-img" />
        </div>
      </div>
    </section>

    <!-- ─── 02 Demo 影片：承接 Hero 的次要 CTA ─── -->
    <section id="demo" class="home-section home-section-alt">
      <div class="home-container text-center">
        <p class="home-label">{{ t('pages.home.demo.label') }}</p>
        <h2 class="home-section-title">{{ t('pages.home.demo.title') }}</h2>
        <div class="home-video">
          <iframe
            src="https://www.youtube.com/embed/TKqnpUNT9rg"
            title="kikuflow demo"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </section>

    <!-- ─── 03 四個具名流程：版面照搬 /superpowers，文案換成改版後的四條流程 ─── -->
    <section class="home-section">
      <div class="container">
        <div class="text-center">
          <h2 class="display-5 fw-bold text-dark">{{ t('pages.home.use-cases.title') }}</h2>
          <p class="lead text-secondary mx-auto mt-3" style="max-width: 650px;">{{ t('pages.home.use-cases.subtitle') }}</p>
        </div>
        <div class="mx-auto mt-5" style="max-width: 1024px;">
          <ul class="nav nav-pills justify-content-center flex-wrap gap-2" id="pills-tab" role="tablist">
            <li class="nav-item" role="presentation">
              <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'finance' }" @click="activeTab = 'finance'" type="button">{{ t('pages.home.use-cases.tabs.finance') }}</button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'hr' }" @click="activeTab = 'hr'" type="button">{{ t('pages.home.use-cases.tabs.hr') }}</button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'sales' }" @click="activeTab = 'sales'" type="button">{{ t('pages.home.use-cases.tabs.sales') }}</button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'service' }" @click="activeTab = 'service'" type="button">{{ t('pages.home.use-cases.tabs.service') }}</button>
            </li>
          </ul>
          <div class="mt-5">
            <!-- Tab 1: 費用報銷 -->
            <div v-show="activeTab === 'finance'" class="tab-content">
              <div class="row g-5 align-items-start">
                <div class="col-md-6 order-md-2">
                  <h3 class="fs-2 fw-bold">{{ t('pages.home.use-cases.finance.title') }}</h3>
                  <p class="mt-3 text-secondary" v-html="t('pages.home.use-cases.finance.description')"></p>
                  <!-- 財務費用申請流程圖 -->
                  <div class="finance-expense-flowchart mt-4">
                    <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                      <!-- 步驟 1: 員工提交請款表單（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-file-invoice-dollar" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.finance.flowchart.step1') }}</h5>
                      </div>

                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 2: 直屬主管審批（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-user-check" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.finance.flowchart.step2') }}</h5>
                      </div>

                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 3: AI 會計科目分類（紫色） -->
                      <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-robot" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.finance.flowchart.step3') }}</h5>
                      </div>

                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 4: 財務部門批准（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-check-circle" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.finance.flowchart.step4') }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 order-md-1">
                  <!-- 財務費用申請表單 -->
                  <div class="finance-expense-form-preview">
                    <div class="bg-white p-4 rounded-3 shadow-sm border">
                      <div class="text-center mb-4">
                        <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages.home.use-cases.finance.form.title') }}</h4>
                      </div>

                      <form>
                        <!-- 申請人姓名與所屬部門 並列顯示 -->
                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.finance.form.employee-name') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.finance.form.sample-name')" readonly>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.finance.form.department') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.finance.form.sample-department')" readonly>
                          </div>
                        </div>

                        <!-- 申請金額 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.finance.form.amount') }}</label>
                          <input type="text" class="form-control" :value="t('pages.home.use-cases.finance.form.sample-amount')" readonly>
                        </div>

                        <!-- 費用說明 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.finance.form.description') }}</label>
                          <textarea class="form-control" readonly>{{ t('pages.home.use-cases.finance.form.sample-description') }}</textarea>
                        </div>

                        <!-- 附件上傳區域 (顯示用) -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.finance.form.attachment-upload') }}</label>
                          <div class="border rounded-3 p-3 bg-light text-center">
                            <i class="fas fa-file-invoice text-muted me-2"></i>
                            <span class="text-muted small">{{ t('pages.home.use-cases.finance.form.sample-attachment') }}</span>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Tab 2: 人才招募 -->
            <div v-show="activeTab === 'hr'" class="tab-content">
              <div class="row g-5 align-items-start">
                <div class="col-md-6 order-md-2">
                  <h3 class="fs-2 fw-bold">{{ t('pages.home.use-cases.hr.title') }}</h3>
                  <p class="mt-3 text-secondary" v-html="t('pages.home.use-cases.hr.description')"></p>
                  <!-- 人資招募流程圖 -->
                  <div class="hr-recruitment-flowchart mt-4">
                    <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                      <!-- 步驟 1: 應徵者發起表單（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-user-plus" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.hr.flowchart.step1') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 2: AI 審閱並總結（紫色） -->
                      <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-robot" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.hr.flowchart.step2') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 3: 人資部門評選（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-user-tie" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.hr.flowchart.step3') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 4: 用人主管面試（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-handshake" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.hr.flowchart.step4') }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 order-md-1">
                  <!-- 人資應徵表單 -->
                  <div class="hr-application-form-preview">
                    <div class="bg-white p-4 rounded-3 shadow-sm border">
                      <div class="text-center mb-4">
                        <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages.home.use-cases.hr.form.title') }}</h4>
                      </div>

                      <form>
                        <!-- 應徵者姓名與聯絡電話 並列顯示 -->
                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.hr.form.applicant-name') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.hr.form.sample-name')" readonly>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.hr.form.phone') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.hr.form.sample-phone')" readonly>
                          </div>
                        </div>

                        <!-- 應徵職位 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.hr.form.position') }}</label>
                          <input type="text" class="form-control" :value="t('pages.home.use-cases.hr.form.sample-position')" readonly>
                        </div>

                        <!-- 工作經驗年數 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.hr.form.experience') }}</label>
                          <input type="text" class="form-control" :value="t('pages.home.use-cases.hr.form.sample-experience')" readonly>
                        </div>

                        <!-- 履歷上傳區域 (顯示用) -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.hr.form.resume-upload') }}</label>
                          <div class="border rounded-3 p-3 bg-light text-center">
                            <i class="fas fa-file-pdf text-muted me-2"></i>
                            <span class="text-muted small">{{ t('pages.home.use-cases.hr.form.sample-resume') }}</span>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Tab 3: 報價訂單 -->
            <div v-show="activeTab === 'sales'" class="tab-content">
              <div class="row g-5 align-items-start">
                <div class="col-md-6 order-md-2">
                  <h3 class="fs-2 fw-bold">{{ t('pages.home.use-cases.sales.title') }}</h3>
                  <p class="mt-3 text-secondary" v-html="t('pages.home.use-cases.sales.description')"></p>
                  <!-- 業務報價流程圖 -->
                  <div class="sales-quote-flowchart mt-4">
                    <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                      <!-- 步驟 1（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-file-alt" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.sales.flowchart.step1') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 2: AI（紫色） -->
                      <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-robot" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.sales.flowchart.step2') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 3（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-user-check" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.sales.flowchart.step3') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 4（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-shipping-fast" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.sales.flowchart.step4') }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 order-md-1">
                  <!-- 業務報價申請表單 -->
                  <div class="sales-quote-form-preview">
                    <div class="bg-white p-4 rounded-3 shadow-sm border">
                      <div class="text-center mb-4">
                        <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages.home.use-cases.sales.form.title') }}</h4>
                      </div>

                      <form>
                        <!-- 業務姓名與客戶名稱 並列顯示 -->
                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.sales-name') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.sales.form.sample-sales-name')" readonly>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.customer-name') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.sales.form.sample-customer-name')" readonly>
                          </div>
                        </div>

                        <!-- 產品名稱 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.product-name') }}</label>
                          <input type="text" class="form-control" :value="t('pages.home.use-cases.sales.form.sample-product-name')" readonly>
                        </div>

                        <!-- 數量與單價 並列顯示 -->
                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.quantity') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.sales.form.sample-quantity')" readonly>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.unit-price') }}</label>
                            <input type="text" class="form-control" :value="t('pages.home.use-cases.sales.form.sample-unit-price')" readonly>
                          </div>
                        </div>

                        <!-- 總金額 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.total-amount') }}</label>
                          <input type="text" class="form-control fw-bold text-success" :value="t('pages.home.use-cases.sales.form.sample-total-amount')" readonly>
                        </div>

                        <!-- 備註說明 -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.notes') }}</label>
                          <textarea class="form-control" readonly>{{ t('pages.home.use-cases.sales.form.sample-notes') }}</textarea>
                        </div>

                        <!-- 附件上傳區域 (顯示用) -->
                        <div class="mb-3">
                          <label class="form-label fw-medium text-secondary">{{ t('pages.home.use-cases.sales.form.related-documents') }}</label>
                          <div class="border rounded-3 p-3 bg-light text-center">
                            <i class="fas fa-file-pdf text-muted me-2"></i>
                            <span class="text-muted small">{{ t('pages.home.use-cases.sales.form.sample-document') }}</span>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
             <!-- Tab 4: 客訴退貨 -->
            <div v-show="activeTab === 'service'" class="tab-content">
              <div class="row g-5 align-items-start">
                <div class="col-md-6 order-md-2">
                  <h3 class="fs-2 fw-bold">{{ t('pages.home.use-cases.service.title') }}</h3>
                  <p class="mt-3 text-secondary" v-html="t('pages.home.use-cases.service.description')"></p>
                  <!-- 客戶服務流程圖 -->
                  <div class="customer-service-flowchart mt-4">
                    <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                      <!-- 步驟 1（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-user" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.service.flowchart.step1') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 2: AI（紫色） -->
                      <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-robot" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.service.flowchart.step2') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 3（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-users" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.service.flowchart.step3') }}</h5>
                      </div>
                      <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                      <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                      <!-- 步驟 4（藍色） -->
                      <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                        <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                          <i class="fas fa-archive" style="font-size: 18px;"></i>
                        </div>
                        <h5 class="small fw-semibold mb-1">{{ t('pages.home.use-cases.service.flowchart.step4') }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 order-md-1">
                  <!-- 客戶服務表單 -->
                  <div class="customer-service-preview">
                    <div class="bg-white p-4 rounded-3 shadow-sm border">
                      <div class="text-center mb-4">
                        <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages.home.use-cases.service.form.title') }}</h4>
                      </div>
                    
                      <div class="form-preview">
                        <!-- 客戶資訊 -->
                        <div class="row g-3">
                          <div class="col-md-6">
                            <label class="form-label small text-secondary fw-medium">{{ t('pages.home.use-cases.service.form.customer-name') }}</label>
                            <div class="form-control form-control-sm bg-light text-muted">{{ t('pages.home.use-cases.service.form.sample-name') }}</div>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label small text-secondary fw-medium">{{ t('pages.home.use-cases.service.form.contact-phone') }}</label>
                            <div class="form-control form-control-sm bg-light text-muted">{{ t('pages.home.use-cases.service.form.sample-phone') }}</div>
                          </div>
                        </div>
                      
                        <!-- 服務類型 -->
                        <div class="mt-3">
                          <label class="form-label small text-secondary fw-medium">{{ t('pages.home.use-cases.service.form.service-type') }}</label>
                          <div class="form-control form-control-sm bg-light text-muted">
                            <i class="fas fa-exclamation-triangle text-warning me-2"></i>{{ t('pages.home.use-cases.service.form.service-complaint') }}
                          </div>
                        </div>
                      
                        <!-- 問題描述 -->
                        <div class="mt-3">
                          <label class="form-label small text-secondary fw-medium">{{ t('pages.home.use-cases.service.form.problem-description') }}</label>
                          <div class="form-control bg-light text-muted" style="height: 80px; font-size: 0.875rem; line-height: 1.4;">
                            {{ t('pages.home.use-cases.service.form.sample-description') }}
                          </div>
                        </div>
                      
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p class="home-flows-footer">
            <a :href="localePath('/superpowers')" class="home-link">{{ t('pages.home.use-cases.more-link') }}</a>
          </p>
          <p class="home-flows-footnote">{{ t('pages.home.use-cases.footnote') }}</p>
        </div>
      </div>
    </section>

    <!-- ─── 04 市場座標：不是簽核系統，也不是自動化工具 ─── -->
    <section class="home-section home-section-alt">
      <div class="home-container">
        <h2 class="home-section-title text-center">{{ t('pages.home.position.title') }}</h2>

        <div class="home-contrast">
          <div class="home-contrast-item">
            <h3 class="home-contrast-title">{{ t('pages.home.position.not-approval.title') }}</h3>
            <p class="home-contrast-body" v-html="t('pages.home.position.not-approval.body')"></p>
          </div>
          <div class="home-contrast-item">
            <h3 class="home-contrast-title">{{ t('pages.home.position.not-automation.title') }}</h3>
            <p class="home-contrast-body" v-html="t('pages.home.position.not-automation.body')"></p>
          </div>
        </div>

        <div class="home-pillars">
          <div class="home-pillar">
            <img :src="feature02" :alt="t('pages.home.position.pillar1.alt')" class="home-pillar-img" />
            <h3 class="home-pillar-title">{{ t('pages.home.position.pillar1.title') }}</h3>
            <p class="home-pillar-body">{{ t('pages.home.position.pillar1.body') }}</p>
          </div>
          <div class="home-pillar">
            <!-- feature-01 畫的就是「用講的 → 產出流程圖」，對應的是這一根支柱 -->
            <img :src="feature01" :alt="t('pages.home.position.pillar2.alt')" class="home-pillar-img" />
            <h3 class="home-pillar-title">{{ t('pages.home.position.pillar2.title') }}</h3>
            <p class="home-pillar-body">{{ t('pages.home.position.pillar2.body') }}</p>
          </div>
          <div class="home-pillar">
            <img :src="feature03" :alt="t('pages.home.position.pillar3.alt')" class="home-pillar-img" />
            <h3 class="home-pillar-title">{{ t('pages.home.position.pillar3.title') }}</h3>
            <p class="home-pillar-body">{{ t('pages.home.position.pillar3.body') }}</p>
          </div>
        </div>

        <p class="text-center">
          <a :href="localePath('/comparison')" class="home-link">{{ t('pages.home.position.compare-link') }}</a>
        </p>
      </div>
    </section>

    <!-- ─── 05 誰適合用：自我篩選，一屏三行 ─── -->
    <section class="home-section">
      <div class="home-container">
        <h2 class="home-fit-title">{{ t('pages.home.fit.title') }}</h2>
        <ul class="home-fit-list">
          <li>{{ t('pages.home.fit.item1') }}</li>
          <li>{{ t('pages.home.fit.item2') }}</li>
          <li>{{ t('pages.home.fit.item3') }}</li>
        </ul>
      </div>
    </section>

    <!-- ─── 06 定價：價格透明是對抗傳統廠商的武器，不藏在另一頁 ─── -->
    <section class="home-section home-section-alt">
      <div class="home-container text-center">
        <h2 class="home-section-title">{{ t('pages.home.pricing.title') }}</h2>
        <p class="home-price">
          <strong>{{ t('pages.home.pricing.price') }}</strong>{{ t('pages.home.pricing.unit') }}
        </p>
        <p class="home-price-note">{{ t('pages.home.pricing.line1') }}</p>
        <p class="home-price-note">{{ t('pages.home.pricing.line2') }}</p>
        <p>
          <a :href="localePath('/pricing')" class="home-link">{{ t('pages.home.pricing.link') }}</a>
        </p>
      </div>
    </section>

    <!-- ─── 07 四個小問答：疑慮在看到價格的那一刻最強烈，所以緊接在定價下方 ─── -->
    <section class="home-section">
      <div class="home-container">
        <h2 class="home-section-title text-center">{{ t('pages.home.faq.title') }}</h2>
        <div class="home-faq">
          <div v-for="n in 4" :key="n" class="home-faq-item">
            <p class="home-faq-q">{{ t(`pages.home.faq.q${n}`) }}</p>
            <p class="home-faq-a">{{ t(`pages.home.faq.a${n}`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── 08 三步驟 + 最終 CTA ─── -->
    <section class="home-section home-section-alt">
      <div class="home-container">
        <h2 class="home-section-title text-center">{{ t('pages.home.start.title') }}</h2>

        <div class="home-steps">
          <template v-for="n in 3" :key="n">
            <div class="home-step">
              <div class="home-step-num">0{{ n }}</div>
              <h3 class="home-step-title">{{ t(`pages.home.start.step${n}.title`) }}</h3>
              <p class="home-step-body" v-html="t(`pages.home.start.step${n}.body`)"></p>
            </div>
            <div v-if="n < 3" class="home-step-arrow">→</div>
          </template>
        </div>

        <!-- 降低承諾感，避免看起來像要整間公司一起搬 -->
        <p class="home-steps-note">{{ t('pages.home.start.note') }}</p>

        <div class="home-final">
          <h2 class="home-final-title">{{ t('pages.home.start.cta-title') }}</h2>
          <div class="home-final-actions">
            <a v-if="!authStore.isLoggedIn" :href="localePath('/signup')" class="landing-btn landing-btn-lg">
              {{ t('pages.home.start.cta-primary') }}
            </a>
            <a v-else href="/app" class="landing-btn landing-btn-lg">
              {{ t('common.enter-dashboard') }}
            </a>
            <a :href="localePath('/contact')" class="landing-btn landing-btn-lg landing-btn-outline">
              {{ t('pages.home.start.cta-secondary') }}
            </a>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
// 透過 import 引入圖片，Vite build 時會自動加上 content hash 避免 cache 問題
import feature01 from '../assets/images/feature-01.jpg'
import feature02 from '../assets/images/feature-02.jpg'
import feature03 from '../assets/images/feature-03.jpg'
// Hero 的產品截圖裡有介面文字與流程名稱，四個語系各一張
import heroEn from '../assets/images/hero-app-en.webp'
import heroZhTw from '../assets/images/hero-app-zh-tw.webp'
import heroZhCn from '../assets/images/hero-app-zh-cn.webp'
import heroJa from '../assets/images/hero-app-ja.webp'

const { t, locale } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

const HERO_SHOTS: Record<string, string> = {
  en: heroEn,
  'zh-tw': heroZhTw,
  'zh-cn': heroZhCn,
  ja: heroJa
}
const heroShot = computed(() => HERO_SHOTS[locale.value] ?? heroEn)

const LANGUAGES = ['en', 'zh-tw', 'zh-cn', 'ja']

// 英文站在根路徑不帶前綴，其餘語言帶 /{lang}；與導覽列的連結寫法一致
const localePath = (path: string) => {
  const lang = route.params.lang as string | undefined
  return lang && LANGUAGES.includes(lang) ? `/${lang}${path}` : path
}

// 四個具名流程的分頁切換
const activeTab = ref('finance')
</script>

<style scoped>
.home {
  font-family: 'Noto Sans TC', 'PingFang TC', 'Microsoft JhengHei', sans-serif;
  color: #1a1a1a;
  line-height: 1.8;
}

.home-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}


.text-center { text-align: center; }

.home-section { padding: 6rem 0; }
.home-section-alt { background-color: #fafafa; }

.home-label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #aaa;
  margin-bottom: 0.5rem;
}

.home-section-title {
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 1.2rem;
}

.home-section-sub {
  font-size: 1.05rem;
  color: #666;
  margin-bottom: 0;
}

.home-link {
  color: #1a1a1a;
  font-weight: 600;
  text-decoration: none;
}

.home-link:hover { text-decoration: underline; }

/* ─── 01 Hero ─── */
.home-hero {
  padding: 8rem 0 5rem;
  background-color: #fff;
}

.home-hero-grid {
  display: flex;
  align-items: center;
  gap: 3rem;
  max-width: 1180px;
}

/* 截圖給多一點寬度，關卡與負責人才看得清楚 */
.home-hero-text { flex: 1; }
.home-hero-shot { flex: 1.2; min-width: 0; }

.home-hero-title {
  font-size: clamp(1.9rem, 4.2vw, 2.9rem);
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.01em;
  margin-bottom: 1.25rem;
}

.home-hero-sub {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.95;
  margin-bottom: 0.75rem;
}

.home-hero-audience {
  font-size: 0.95rem;
  color: #888;
  margin-bottom: 2rem;
}

.home-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

/* 產品截圖：加一圈細框與陰影，跟純白的 hero 背景分開 */
.home-hero-img {
  width: 100%;
  display: block;
  border: 1px solid #e6e6e6;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
}


/* ─── 02 影片 ─── */
.home-video {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  margin-top: 2.5rem;
  border-radius: 16px;
  overflow: hidden;
}

.home-video iframe {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
}

/* ─── 03 四個流程（樣式取自 /superpowers，兩頁的流程圖長得一樣）─── */
/* 一般關卡用藍色 */
.flowchart-node-blue {
  background-color: #dbeafe !important;
  border-color: #3b82f6 !important;
}

.flowchart-node-blue .icon-wrapper { background-color: #3b82f6 !important; }
.flowchart-node-blue h5 { color: #1e40af !important; }

/* AI 執行的關卡用紫色，一眼看得出哪幾關不需要人 */
.flowchart-node-purple {
  background-color: #ede9fe !important;
  border-color: #7c3aed !important;
}

.flowchart-node-purple .icon-wrapper { background-color: #7c3aed !important; }
.flowchart-node-purple h5 { color: #5b21b6 !important; }

/* 四格平均分寬，長標題不撐破版面 */
.flowchart-container > .flowchart-node { min-width: 0; }

/* 表單模擬畫面的唯讀欄位 */
.form-control:read-only,
.form-select:disabled {
  background-color: #f8f9fa;
  border-color: #e9ecef;
}

.home-flows-footer {
  text-align: center;
  margin: 3.5rem 0 0.5rem;
}

.home-flows-footnote {
  text-align: center;
  font-size: 0.9rem;
  color: #999;
  margin: 0;
}

/* ─── 04 市場座標 ─── */
.home-contrast {
  display: flex;
  gap: 3rem;
  margin: 3rem 0 4.5rem;
}

.home-contrast-item { flex: 1; }

.home-contrast-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.9rem;
}

.home-contrast-body {
  font-size: 1rem;
  color: #555;
  line-height: 1.95;
  margin: 0;
}

.home-pillars {
  display: flex;
  gap: 2rem;
  margin-bottom: 3rem;
}

.home-pillar { flex: 1; min-width: 0; }

/* 三張插圖的原始比例差很多，統一裝進同尺寸的框，三根支柱的標題才會對齊 */
.home-pillar-img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: contain;
  background: #fff;
  border-radius: 12px;
  display: block;
  margin-bottom: 1.25rem;
}

.home-pillar-title {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.home-pillar-body {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.85;
  margin: 0;
}

/* ─── 05 誰適合用 ─── */
.home-fit-title {
  font-size: clamp(1.3rem, 2.5vw, 1.7rem);
  font-weight: 700;
  margin-bottom: 1.75rem;
}

.home-fit-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.home-fit-list li {
  position: relative;
  padding-left: 1.6rem;
  font-size: 1.05rem;
  color: #555;
  line-height: 2.2;
}

.home-fit-list li::before {
  content: '・';
  position: absolute;
  left: 0;
  color: #999;
}

/* ─── 06 定價 ─── */
.home-price {
  font-size: 1.1rem;
  color: #666;
  margin: 1.5rem 0 1rem;
}

.home-price strong {
  font-size: clamp(2.2rem, 5vw, 3rem);
  font-weight: 700;
  color: #1a1a1a;
}

.home-price-note {
  font-size: 1rem;
  color: #666;
  margin-bottom: 0.3rem;
}

.home-price-note + p { margin-top: 1.5rem; }

/* ─── 07 四個小問答 ─── */
.home-faq { margin-top: 2.5rem; }

.home-faq-item {
  padding: 1.4rem 0;
  border-bottom: 1px solid #ededed;
}

.home-faq-item:last-child { border-bottom: none; }

.home-faq-q {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
}

.home-faq-a {
  font-size: 1rem;
  color: #666;
  line-height: 1.9;
  margin: 0;
}

/* ─── 08 三步驟與最終 CTA ─── */
.home-steps {
  display: flex;
  align-items: stretch;
  gap: 1.5rem;
  margin: 3rem 0 1.5rem;
}

.home-step {
  flex: 1;
  text-align: center;
  padding: 2rem 1.5rem;
  border: 1.5px solid #e8e8e8;
  border-radius: 16px;
  background: #fff;
}

.home-step-arrow {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 1.8rem;
  color: #ccc;
}

.home-step-num {
  font-size: 2.5rem;
  font-weight: 700;
  color: #e8e8e8;
  line-height: 1;
  margin-bottom: 0.75rem;
}

.home-step-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.home-step-body {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.8;
  margin: 0;
}

.home-steps-note {
  text-align: center;
  font-size: 0.95rem;
  color: #888;
  margin-bottom: 4rem;
}

.home-final { text-align: center; }

.home-final-title {
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 600;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.home-final-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

/* ─── RWD ─── */
@media (max-width: 900px) {
  .home-contrast,
  .home-pillars {
    flex-direction: column;
  }

  .home-contrast { gap: 2rem; }
}

@media (max-width: 768px) {
  .home-section { padding: 4rem 0; }

  .home-hero {
    padding: 5rem 0 3.5rem;
  }

  .home-hero-grid {
    flex-direction: column;
    gap: 2.5rem;
  }

  /* 手機上讓截圖排在文案之後，先讀到主張再看畫面 */
  .home-hero-shot { width: 100%; }


  .home-steps {
    flex-direction: column;
    gap: 1rem;
  }

  .home-step-arrow {
    transform: rotate(90deg);
    justify-content: center;
    width: 100%;
  }
}
</style>
